import { IAPIRoutes } from "../../../../config";
import { getDevicelessApiEndpoint } from "../../../../util/devicelessRouting";

export type IAlertRepository = ReturnType<typeof AlertRepositoryFactory>;

export const AlertRepositoryFactory = ({
  APIProvider,
  AuthService,
  APIRoutes,
}: {
  APIProvider: IAPIProvider;
  AuthService: IAuthService;
  APIRoutes: IAPIRoutes;
}) => {
  const { GET, POST, PUT } = APIProvider;
  const { getAuthenticationToken } = AuthService;

  const getAlertListAPI: IGetAlertListAPI = async (payload) =>
    GET(
      APIRoutes.apiGetAlertListNew.replace(
        "{clinic_id}",
        payload.clinic_id?.toString()
      ),
      payload,
      {
        Authorization: `Bearer ${await getAuthenticationToken()}`,
      }
    );

  const addAlertComment: IAddAlertCommentAPI = async ({
    clinic_id,
    alert_id,
    ...payload
  }) => {
    return POST(
      APIRoutes.apiAddAlertComment
        .replace("{clinic_id}", clinic_id)
        .replace("{alert_id}", alert_id),
      { ...payload },
      {
        Authorization: `Bearer ${await getAuthenticationToken()}`,
      }
    );
  };

  const getAlertDetails: IGetAlertDetailAPI = async ({
    clinicId,
    residentId,
    alertId,
  }) =>
    GET(
      APIRoutes.apiGetAlertDetail
        .replace("{clinic_id}", clinicId?.toString())
        .replace("{resident_id}", residentId?.toString())
        .replace("{alert_id}", alertId?.toString()),
      {},
      {
        Authorization: `Bearer ${await getAuthenticationToken()}`,
      }
    );

  const getCombinedAlertAPI: IGetCombinedAlertListAPI = async (payload) =>
    GET(
      APIRoutes.apiGetCombinedAlertList.replace(
        "{clinic_id}",
        payload.clinic_id?.toString()
      ),
      payload,
      {
        Authorization: `Bearer ${await getAuthenticationToken()}`,
      }
    );

  const getFlagAlertClinicalAPI: IGetFlagAlertClinicalAPI = async (payload) => {
    const endpoint = getDevicelessApiEndpoint(
      APIRoutes.apiGetFlagAlertClinical
    );
    return GET(
      endpoint.replace("{clinic_id}", payload.clinic_id?.toString()),
      payload,
      {
        Authorization: `Bearer ${await getAuthenticationToken()}`,
      }
    );
  };

  const getFlagListAPI: IGetFlagListAPI = async (payload) =>
    GET(
      APIRoutes.apiGetFlagList.replace(
        "{clinic_id}",
        payload.clinic_id?.toString()
      ),
      payload,
      {
        Authorization: `Bearer ${await getAuthenticationToken()}`,
      }
    );

  const getInboxFlagsByReviewStatusAPI: IGetInboxFlagsByReviewStatusAPI =
    async (payload) => {
      const endpoint = getDevicelessApiEndpoint(
        APIRoutes.apiGetInboxFlagsByReviewStatus
      );
      return GET(
        endpoint.replace("{clinic_id}", payload.clinic_id?.toString()),
        payload,
        {
          Authorization: `Bearer ${await getAuthenticationToken()}`,
        }
      );
    };

  const getPatientFlagsAPI: IGetPatientFlagsAPI = async (payload) => {
    const endpoint = getDevicelessApiEndpoint(APIRoutes.apiGetPatientFlags);
    return GET(
      endpoint
        .replace("{clinic_id}", payload.clinic_id?.toString())
        .replace("{patient_id}", payload.patient_id?.toString()),
      {
        page: payload.page,
        page_size: payload.page_size,
        ...(payload.event_date && { event_date: payload.event_date }),
      },
      {
        Authorization: `Bearer ${await getAuthenticationToken()}`,
      }
    );
  };

  const getPatientAlertAPI: IGetPatientAlertAPI = async (payload) => {
    const endpoint = getDevicelessApiEndpoint(APIRoutes.apiPatientAlert);
    return GET(
      endpoint
        .replace("{patient_id}", payload.patient_id?.toString())
        .replace("{clinic_id}", payload.clinic_id?.toString()),
      payload,
      {
        Authorization: `Bearer ${await getAuthenticationToken()}`,
      }
    );
  };

  const addAlertAck: IAddAlertAckAPI = async ({ clinic_id, ...payload }) => {
    const endpoint = getDevicelessApiEndpoint(APIRoutes.apiAddAlertAck);
    return PUT(
      endpoint.replace("{clinic_id}", clinic_id.toString()),
      { ...payload },
      {
        Authorization: `Bearer ${await getAuthenticationToken()}`,
      }
    );
  };

  const hideFlag: IHideFlagAPI = async ({ clinic_id, ...payload }) => {
    return PUT(
      APIRoutes.apiHideFlag
        .replace("{clinic_id}", clinic_id.toString())
        .replace("{flagged_id}", payload.flagged_id.toString()),
      { ...payload },
      {
        Authorization: `Bearer ${await getAuthenticationToken()}`,
      }
    );
  };

  return {
    getAlertListAPI,
    getAlertDetails,
    addAlertComment,
    getCombinedAlertAPI,
    addAlertAck,
    getPatientAlertAPI,
    getFlagListAPI,
    getFlagAlertClinicalAPI,
    hideFlag,
    getInboxFlagsByReviewStatusAPI,
    getPatientFlagsAPI,
  };
};
